import { ShieldCheck } from "lucide-react";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { refreshActivity, type RefreshActivity } from "@/services/api/http-client/refresh-activity";

const SHOW_DELAY_MS = 400;
const MIN_VISIBLE_MS = 650;

interface Props {
  initialCheck: boolean;
  activity?: RefreshActivity;
}

export function SessionRefreshIndicator({ initialCheck, activity = refreshActivity }: Props) {
  const refreshing = useSyncExternalStore(activity.subscribe, activity.getSnapshot, () => false);
  const active = refreshing || initialCheck;
  const [visible, setVisible] = useState(false);
  const [mode, setMode] = useState<"initial" | "background">("background");
  const visibleSince = useRef(0);

  useEffect(() => {
    if (active) {
      if (visible) return;
      const timer = window.setTimeout(() => {
        visibleSince.current = Date.now();
        setMode(initialCheck ? "initial" : "background");
        setVisible(true);
      }, SHOW_DELAY_MS);
      return () => window.clearTimeout(timer);
    }
    if (!visible) return;
    const remaining = Math.max(0, MIN_VISIBLE_MS - (Date.now() - visibleSince.current));
    const timer = window.setTimeout(() => setVisible(false), remaining);
    return () => window.clearTimeout(timer);
  }, [active, initialCheck, visible]);

  if (!visible) return null;

  if (mode === "initial") {
    return (
      <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-background/90 px-5 backdrop-blur-md duration-300 animate-in fade-in motion-reduce:animate-none">
        <div
          role="status"
          aria-live="polite"
          className="relative w-full max-w-sm overflow-hidden rounded-[2rem] border border-border bg-card p-8 text-center shadow-2xl sm:p-10"
        >
          <div className="pointer-events-none absolute -right-14 -top-14 size-40 rounded-full bg-primary/10 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-20 -left-12 size-40 rounded-full bg-primary/5 blur-3xl" />
          <div className="relative mx-auto mb-7 flex size-20 items-center justify-center rounded-[1.4rem] border border-primary/15 bg-primary/5 shadow-sm">
            <div className="absolute inset-0 rounded-[1.4rem] border border-transparent border-t-primary/70 motion-safe:animate-[spin_3s_linear_infinite]" />
            <ShieldCheck
              aria-hidden="true"
              className="size-9 text-primary motion-safe:animate-pulse"
              strokeWidth={1.6}
            />
          </div>
          <p className="mb-2 font-mono text-[10px] font-semibold uppercase tracking-[0.24em] text-primary">
            Acesso seguro
          </p>
          <h2 className="text-xl font-semibold tracking-tight text-foreground">
            Preparando seu acesso
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            Estamos confirmando sua sessão. Você já pode continuar em instantes.
          </p>
          <div className="mx-auto mt-7 h-1 w-32 overflow-hidden rounded-full bg-primary/10">
            <div className="h-full w-1/2 rounded-full bg-primary motion-safe:animate-[pulse_1.2s_ease-in-out_infinite]" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-5 right-5 z-[9998] flex max-w-[calc(100vw-2.5rem)] items-center gap-3 rounded-full border border-border bg-card px-5 py-3 shadow-xl duration-300 animate-in fade-in slide-in-from-bottom-2 motion-reduce:animate-none sm:bottom-6 sm:right-6"
    >
      <div className="relative flex size-9 shrink-0 items-center justify-center rounded-full bg-primary/10">
        <div className="absolute inset-0 rounded-full border border-transparent border-t-primary motion-safe:animate-spin" />
        <ShieldCheck aria-hidden="true" className="size-4 text-primary" strokeWidth={1.8} />
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold leading-tight text-foreground">
          Reconectando sua sessão
        </p>
        <p className="mt-0.5 text-[11px] text-muted-foreground">
          Você pode continuar usando o sistema
        </p>
      </div>
    </div>
  );
}
